import React, { useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Loader2, Activity, ThumbsUp, ThumbsDown, ExternalLink,
  Quote, ChevronDown, ChevronUp, Filter, Wand2,
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_PILL = {
  sent: 'bg-emerald-500/20 text-emerald-300',
  failed: 'bg-red-500/20 text-red-300',
  skipped: 'bg-amber-500/20 text-amber-300',
  pending: 'bg-white/5 text-white/40',
};

const FeedbackButton = ({ active, kind, onClick, testId }) => {
  const isPos = kind === 'positive';
  const Icon = isPos ? ThumbsUp : ThumbsDown;
  const activeClasses = isPos
    ? 'bg-emerald-500/20 text-emerald-300'
    : 'bg-red-500/20 text-red-300';
  const idleClasses = isPos
    ? 'text-white/30 hover:text-emerald-400 hover:bg-white/5'
    : 'text-white/30 hover:text-red-400 hover:bg-white/5';
  const titles = isPos
    ? { off: 'Mark as correct match', on: 'Remove thumbs up' }
    : { off: 'Mark as false positive', on: 'Remove thumbs down' };
  return (
    <button
      onClick={onClick}
      title={active ? titles.on : titles.off}
      className={`p-1.5 rounded transition-colors ${active ? activeClasses : idleClasses}`}
      data-testid={testId}
    >
      <Icon className="w-3.5 h-3.5" />
    </button>
  );
};

/**
 * Pull a ~3-sentence snippet around the reason keywords from a full transcript.
 * Used as a fallback for legacy actions that don't have `snippet` stored.
 */
const synthesiseSnippet = (transcript, reason) => {
  if (!transcript) return '';
  const sentences = transcript
    .replace(/\s+/g, ' ')
    .split(/(?<=[.!?])\s+/)
    .filter(s => s.trim().length > 0);
  if (sentences.length === 0) return transcript.slice(0, 600);

  // Take the 3-5 longest meaningful tokens from the reason as crude keywords.
  const stopwords = new Set([
    'the', 'and', 'for', 'with', 'that', 'this', 'have', 'has', 'was',
    'are', 'were', 'from', 'about', 'their', 'they', 'them', 'said',
    'asked', 'caller', 'agent', 'customer', 'call', 'transcript',
  ]);
  const keywords = (reason || '')
    .toLowerCase()
    .replace(/[^a-z0-9 ]+/g, ' ')
    .split(/\s+/)
    .filter(w => w.length >= 4 && !stopwords.has(w))
    .slice(0, 5);

  // Score each sentence by how many keywords it contains.
  let bestIdx = 0;
  let bestScore = -1;
  sentences.forEach((s, i) => {
    const lower = s.toLowerCase();
    const score = keywords.reduce((acc, k) => acc + (lower.includes(k) ? 1 : 0), 0);
    if (score > bestScore) { bestScore = score; bestIdx = i; }
  });

  // Return the best sentence plus one of context on each side.
  const start = Math.max(0, bestIdx - 1);
  const end = Math.min(sentences.length, bestIdx + 2);
  return sentences.slice(start, end).join(' ');
};

const SnippetSection = ({ action, token }) => {
  const [open, setOpen] = useState(false);
  const [lazySnippet, setLazySnippet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const storedSnippet = (action.snippet || '').trim();

  const fetchTranscriptSnippet = useCallback(async () => {
    if (!action.call_id) {
      setErr('No call linked to this action.');
      return;
    }
    setLoading(true);
    setErr(null);
    try {
      const r = await axios.get(`${API}/calls/${action.call_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const transcript = r.data?.transcription || '';
      if (!transcript) {
        setErr('Transcript is not yet available for this call.');
      } else {
        setLazySnippet(synthesiseSnippet(transcript, action.reason));
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Failed to load transcript');
    } finally {
      setLoading(false);
    }
  }, [action.call_id, action.reason, token]);

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    // Only lazy-load when opening AND we don't already have something to show.
    if (next && !storedSnippet && lazySnippet === null && !loading) {
      fetchTranscriptSnippet();
    }
  };

  const snippetToShow = storedSnippet || lazySnippet || '';

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={handleToggle}
        className="inline-flex items-center gap-1.5 text-[11px] text-white/40 hover:text-teal-300 transition-colors"
        data-testid={`vf-snippet-toggle-${action.id}`}
        title="Show the part of the transcript that triggered this rule"
      >
        <Quote className="w-3 h-3" />
        {open ? 'Hide transcript snippet' : 'View transcript snippet'}
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>

      {open && (
        <div
          className="mt-2 bg-black/30 border-l-2 border-teal-400/40 rounded-r px-3 py-2 text-white/70 text-xs leading-relaxed"
          data-testid={`vf-snippet-body-${action.id}`}
        >
          {loading && (
            <span className="inline-flex items-center gap-1.5 text-white/40">
              <Loader2 className="w-3 h-3 animate-spin" />
              Loading transcript…
            </span>
          )}
          {!loading && err && <span className="text-amber-300/70">{err}</span>}
          {!loading && !err && snippetToShow && (
            <>
              <span className="italic">“{snippetToShow}”</span>
              {!storedSnippet && (
                <span className="block mt-1 text-[10px] text-white/25">
                  (Snippet inferred from transcript — older actions don&apos;t have a stored snippet.)
                </span>
              )}
            </>
          )}
          {!loading && !err && !snippetToShow && (
            <span className="text-white/30">No snippet available.</span>
          )}
        </div>
      )}
    </div>
  );
};

const ActionRow = ({ a, ruleName, onFeedback, onTuneRule, token }) => (
  <div className="bg-white/5 border border-white/10 rounded-xl p-4 hover:border-white/20 transition-colors" data-testid={`vf-action-${a.id}`}>
    <div className="flex items-start justify-between gap-3 mb-2">
      <div>
        <p className="text-white text-sm font-medium">{ruleName}</p>
        {a.call_id ? (
          <Link
            to={`/calls/${a.call_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-teal-400/70 hover:text-teal-300 text-xs font-mono mt-0.5 transition-colors"
            data-testid={`vf-action-call-link-${a.id}`}
            title="Open full call & transcript"
          >
            call {a.call_id.slice(0, 14)}…
            <ExternalLink className="w-3 h-3" />
          </Link>
        ) : (
          <p className="text-white/40 text-xs font-mono mt-0.5">no call linked</p>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300">
          {Math.round((a.confidence || 0) * 100)}%
        </span>
        <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_PILL[a.dispatch_status] || STATUS_PILL.pending}`}>
          {a.dispatch_status}
        </span>
      </div>
    </div>
    {a.reason && <p className="text-white/50 text-xs italic mb-2">{a.reason}</p>}
    {a.extracted && Object.keys(a.extracted).length > 0 && (
      <pre className="text-white/40 text-xs bg-black/20 rounded p-2 overflow-x-auto font-mono">
        {JSON.stringify(a.extracted, null, 2)}
      </pre>
    )}
    {a.dispatch_error && (
      <p className="text-red-400/60 text-xs mt-2 font-mono">Error: {a.dispatch_error}</p>
    )}

    <SnippetSection action={a} token={token} />

    {/* "Tune rule" appears only on FP-flagged cards. It's the shortcut
        from a wrong match to fixing it — pre-fills the rule editor
        with this action's snippet as a negative example. */}
    {a.feedback === 'negative' && onTuneRule && (
      <div className="mt-2">
        <button
          type="button"
          onClick={() => onTuneRule(a)}
          data-testid={`vf-tune-rule-${a.id}`}
          className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full bg-purple-500/15 hover:bg-purple-500/25 text-purple-300 border border-purple-500/20 transition-colors"
          title="Open the rule editor pre-filled with this misfire as a negative example"
        >
          <Wand2 className="w-3 h-3" />
          Tune rule from this action
        </button>
      </div>
    )}

    <div className="flex items-center justify-between mt-2">
      <p className="text-white/20 text-xs">{new Date(a.created_at).toLocaleString()}</p>
      <div className="flex items-center gap-1">
        <FeedbackButton
          active={a.feedback === 'positive'}
          kind="positive"
          testId={`vf-fb-up-${a.id}`}
          onClick={() => onFeedback(a.id, a.feedback === 'positive' ? 'clear' : 'positive')}
        />
        <FeedbackButton
          active={a.feedback === 'negative'}
          kind="negative"
          testId={`vf-fb-down-${a.id}`}
          onClick={() => onFeedback(a.id, a.feedback === 'negative' ? 'clear' : 'negative')}
        />
      </div>
    </div>
  </div>
);

export const ActionsLog = ({ actions, loading, rules, onFeedback, onTuneRule }) => {
  const { token } = useAuth();
  const [falsePositivesOnly, setFalsePositivesOnly] = useState(false);

  const falsePositiveCount = useMemo(
    () => actions.filter(a => a.feedback === 'negative').length,
    [actions],
  );

  const visibleActions = useMemo(
    () => (falsePositivesOnly ? actions.filter(a => a.feedback === 'negative') : actions),
    [actions, falsePositivesOnly],
  );

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 text-white/20 animate-spin" /></div>;
  }
  if (!actions.length) {
    return (
      <div className="text-center py-16 text-white/30">
        <Activity className="w-10 h-10 mx-auto mb-3 text-white/20" />
        <p>No rule matches yet</p>
        <p className="text-white/20 text-xs mt-1">Actions will appear here as new calls are graded</p>
      </div>
    );
  }

  const getRuleName = (id) => rules.find(r => r.id === id)?.name || id.slice(0, 8);

  return (
    <div className="space-y-3">
      {/* Filter bar */}
      <div className="flex items-center gap-2 text-xs">
        <Filter className="w-3.5 h-3.5 text-white/30" />
        <button
          type="button"
          onClick={() => setFalsePositivesOnly(false)}
          data-testid="vf-filter-all"
          className={`px-2.5 py-1 rounded-full border transition-colors ${
            !falsePositivesOnly
              ? 'bg-teal-500/15 border-teal-500/30 text-teal-300'
              : 'bg-white/5 border-white/10 text-white/40 hover:text-white/70'
          }`}
        >
          All <span className="text-white/30 ml-1">{actions.length}</span>
        </button>
        <button
          type="button"
          onClick={() => setFalsePositivesOnly(true)}
          data-testid="vf-filter-false-positives"
          disabled={falsePositiveCount === 0}
          className={`px-2.5 py-1 rounded-full border transition-colors flex items-center gap-1 ${
            falsePositivesOnly
              ? 'bg-red-500/15 border-red-500/30 text-red-300'
              : 'bg-white/5 border-white/10 text-white/40 hover:text-white/70'
          } ${falsePositiveCount === 0 ? 'opacity-40 cursor-not-allowed' : ''}`}
          title={falsePositiveCount === 0
            ? 'Mark actions thumbs-down to flag them as false positives'
            : 'Show only actions flagged thumbs-down'}
        >
          <ThumbsDown className="w-3 h-3" />
          False positives <span className="text-white/30 ml-0.5">{falsePositiveCount}</span>
        </button>
      </div>

      {/* List */}
      {visibleActions.length === 0 ? (
        <div className="text-center py-12 text-white/30" data-testid="vf-actions-empty-fp">
          <ThumbsDown className="w-8 h-8 mx-auto mb-2 text-white/20" />
          <p className="text-sm">No false positives flagged</p>
          <p className="text-white/20 text-xs mt-1">Use the thumbs-down button on any action to mark it a false positive</p>
        </div>
      ) : (
        <div className="space-y-2">
          {visibleActions.map(a => (
            <ActionRow
              key={a.id}
              a={a}
              ruleName={getRuleName(a.rule_id)}
              onFeedback={onFeedback}
              onTuneRule={onTuneRule}
              token={token}
            />
          ))}
        </div>
      )}
    </div>
  );
};
